import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import { StackingAlertBanner } from '@saviynt/design-system';

import CertsAutoRevokeWarning from '../../../components/Banners/AutoRevokeWarning/CertsAutoRevokeWarning';
import {
  setBannerForConsultAndReassign,
  updatePersonalizationApiStatusAction,
} from '../../../redux/certdetails/action';
import {
  ALERT_THEMES,
  PERSONALIZATION_ALERTS,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
  VIEW_OPTIONS,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';

import { invertedMessages } from './messages';

// File order, please maintain or add to:
// - Action Error
// - Info Banner
// - Personalization Banners
// - Auto Revoke Warning

function CertAlerts() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);

  const l = useInvertedLocalize(invertedMessages);
  // Action Error
  const [isActionBannerVisible, setIsActionBannerVisible] = useState(false);
  // Info Banner
  const [showInfoBanner, setShowInfoBanner] = useState(false);
  // Personalization Banners
  const [personalizationColorTheme, setPersonalizationColorTheme] =
    useState('Success');
  const [personalizationTitle, setPersonalizationTitle] = useState('');
  const [personalizationDescription, setPersonalizationDescription] =
    useState('');

  const {
    global: { mediaQueryValue },
    lineActions: {
      criticalAlertMessage: {
        colorTheme: alertTheme,
        description: alertDescription,
        title: alertTitle,
      },
      isActionError,
    },
    personalization: { status, updatePersonalizatonApiStatus, viewOption },
  } = useSelector((state) => state?.certifications?.details);

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mediaQueryValue >= mobileXlargeQuery.value;

  // Action Errors
  useEffect(() => {
    const errorsToCheck = isActionError;

    if (errorsToCheck) setIsActionBannerVisible(true);
    else setIsActionBannerVisible(false);
  }, [isActionError]);

  useEffect(() => {
    // to handle only success alert message
    if (alertDescription && alertTitle && !isActionError)
      setIsActionBannerVisible(true);
  }, [alertTheme, alertDescription, alertTitle]);

  const successLabel =
    messages.CERTS_SUCCESS || localize(intl, getMessageKey('CERTS_SUCCESS'));
  const failedLabel =
    messages.CERTS_FAILED || localize(intl, getMessageKey('CERTS_FAILED'));

  // Personalizaton API
  useEffect(() => {
    switch (updatePersonalizatonApiStatus) {
      case PERSONALIZATION_ALERTS.UPDATE_SUCCESS:
        setPersonalizationColorTheme('Success');
        setPersonalizationTitle(successLabel);
        setPersonalizationDescription(
          messages['PERSONALIZATION.ALERTS.UPDATE_SUCCESS'] ||
            localize(
              intl,
              getMessageKey('PERSONALIZATION.ALERTS.UPDATE_SUCCESS')
            )
        );
        break;
      case PERSONALIZATION_ALERTS.UPDATE_FAILED:
        setPersonalizationColorTheme('Critical');
        setPersonalizationTitle(failedLabel);
        setPersonalizationDescription(
          messages['PERSONALIZATION.ALERTS.UPDATE_FAILED'] ||
            localize(
              intl,
              getMessageKey('PERSONALIZATION.ALERTS.UPDATE_FAILED')
            )
        );
        break;
      case PERSONALIZATION_ALERTS.UPDATE_FAILED_INVALID_LENGTH:
        setPersonalizationColorTheme('Critical');
        setPersonalizationTitle(failedLabel);
        setPersonalizationDescription(
          messages['PERSONALIZATION.ALERTS.UPDATE_FAILED_INVALID_LENGTH'] ||
            localize(
              intl,
              getMessageKey(
                'PERSONALIZATION.ALERTS.UPDATE_FAILED_INVALID_LENGTH'
              )
            )
        );
        break;
      case PERSONALIZATION_ALERTS.CREATE_SUCCESS:
        setPersonalizationColorTheme('Success');
        setPersonalizationTitle(successLabel);
        setPersonalizationDescription(
          messages['PERSONALIZATION.ALERTS.CREATE_SUCCESS'] ||
            localize(
              intl,
              getMessageKey('PERSONALIZATION.ALERTS.CREATE_SUCCESS')
            )
        );
        break;
      case PERSONALIZATION_ALERTS.CREATE_FAILED:
        setPersonalizationColorTheme('Critical');
        setPersonalizationTitle(failedLabel);
        setPersonalizationDescription(
          messages['PERSONALIZATION.ALERTS.CREATE_FAILED'] ||
            localize(
              intl,
              getMessageKey('PERSONALIZATION.ALERTS.CREATE_FAILED')
            )
        );
        break;
      default:
        break;
    }
  }, [updatePersonalizatonApiStatus]);

  // View Options Info
  useEffect(() => {
    if (
      status === 'EXIST_PERSONALIZATION' &&
      (viewOption?.view === VIEW_OPTIONS.DEFAULT_VIEW ||
        viewOption?.view === VIEW_OPTIONS.MY_PERSONALIZED_VIEW)
    )
      setShowInfoBanner(true);
  }, [status, viewOption]);

  const getInfoBannerTitle = () =>
    viewOption?.view === VIEW_OPTIONS.MY_PERSONALIZED_VIEW
      ? `${
          messages[
            'PERSONALIZATION.INFO_BANNER_TITLE_PERSONALIZED.ATTESTATIONS'
          ] ||
          localize(
            intl,
            getMessageKey('PERSONALIZATION.INFO_BANNER_TITLE_PERSONALIZED')
          )
        }`
      : `${
          messages['PERSONALIZATION.DEFAULT_VIEW.ATTESTATIONS'] ||
          localize(intl, getMessageKey('PERSONALIZATION.DEFAULT_VIEW'))
        }`;

  const getInfoBannerDescription = () => {
    if (viewOption?.applied) {
      if (viewOption?.view === VIEW_OPTIONS.MY_PERSONALIZED_VIEW) {
        return `${
          messages[
            'PERSONALIZATION.INFO_BANNER_DESCRIPTION_PERSONALIZED_DESKTOP_FIRST.ATTESTATIONS'
          ] ||
          localize(
            intl,
            getMessageKey(
              'PERSONALIZATION.INFO_BANNER_DESCRIPTION_PERSONALIZED_DESKTOP_FIRST'
            )
          )
        }${
          isMobileXlargeWidth
            ? ''
            : ` ${
                messages[
                  'PERSONALIZATION.INFO_BANNER_DESCRIPTION_PERSONALIZED_DESKTOP_ONLY.ATTESTATIONS'
                ] ||
                localize(
                  intl,
                  getMessageKey(
                    'PERSONALIZATION.INFO_BANNER_DESCRIPTION_PERSONALIZED_DESKTOP_ONLY'
                  )
                )
              }`
        }`;
      }

      return (
        messages['PERSONALIZATION.INFO_BANNER_DESCRIPTION_DEFAULT_VIEW'] ||
        localize(
          intl,
          getMessageKey('PERSONALIZATION.INFO_BANNER_DESCRIPTION_DEFAULT_VIEW')
        )
      );
    }

    return (
      messages['PERSONALIZATION.INFO_BANNER_DESCRIPTION_DEFAULT'] ||
      localize(
        intl,
        getMessageKey('PERSONALIZATION.INFO_BANNER_DESCRIPTION_DEFAULT')
      )
    );
  };

  return (
    <>
      {/* Action Error AlertBanner */}
      <StackingAlertBanner
        colorTheme={alertTheme || ''}
        kind={alertTheme === 'Warning' ? 'low' : undefined}
        title={alertTitle || ''}
        description={l(alertDescription) || ''}
        isVisible={isActionBannerVisible}
        onCancel={() => {
          setIsActionBannerVisible(false);
          dispatch(
            setBannerForConsultAndReassign({
              criticalAlertMessage: {
                title: null,
                description: null,
              },
            })
          );
        }}
        shouldAutoDismiss={alertTheme === ALERT_THEMES.SUCCESS}
      />
      {/* View Options Info / Warning */}
      <StackingAlertBanner
        colorTheme={viewOption?.applied ? 'Information' : 'Warning'}
        kind='low'
        title={getInfoBannerTitle()}
        description={getInfoBannerDescription()}
        isVisible={showInfoBanner}
        onCancel={() => setShowInfoBanner(false)}
        shouldAutoDismiss
        autoDismissDelay={3000}
      />
      {/* Personalization Banners */}
      <StackingAlertBanner
        colorTheme={personalizationColorTheme}
        title={personalizationTitle}
        isColonVisible={false}
        description={personalizationDescription}
        isVisible={updatePersonalizatonApiStatus?.length > 0}
        onCancel={() => dispatch(updatePersonalizationApiStatusAction(''))}
        shouldAutoDismiss
        autoDismissDelay={3000}
      />
      {/* Auto Revoke Warning */}
      <CertsAutoRevokeWarning />
    </>
  );
}

export default CertAlerts;
